import { LAYOUT_SCHEMA_VERSION } from "./grid";
import { GRID_COLUMNS } from "./layoutConfig";
import type { LayoutItem, SignalsDashboardLayoutV1 } from "./types";

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseItem(value: unknown): LayoutItem | null {
    if (!isPlainObject(value)) return null;

    const { objectId, x, y, width, height, autoHeight } = value;
    if (typeof objectId !== "string" || objectId.length === 0) return null;
    if (
        !Number.isInteger(x) ||
        !Number.isInteger(y) ||
        !Number.isInteger(width) ||
        !Number.isInteger(height)
    ) {
        return null;
    }

    return {
        objectId,
        x: x as number,
        y: y as number,
        width: width as number,
        height: height as number,
        ...(autoHeight === true ? { autoHeight: true } : {}),
    };
}

/**
 * Reads an untrusted layout document without throwing. Unusable items are
 * dropped; an unusable document gives null, and the caller falls back to the
 * default layout.
 */
export function safeParseLayout(
    value: unknown,
): SignalsDashboardLayoutV1 | null {
    if (
        !isPlainObject(value) ||
        value.schemaVersion !== LAYOUT_SCHEMA_VERSION ||
        value.columns !== GRID_COLUMNS ||
        !Array.isArray(value.items)
    ) {
        return null;
    }

    const seenObjectIds = new Set<string>();
    const items: LayoutItem[] = [];

    value.items.forEach((rawItem) => {
        const item = parseItem(rawItem);
        if (!item || seenObjectIds.has(item.objectId)) return;

        if (
            item.x < 0 ||
            item.y < 0 ||
            item.width <= 0 ||
            item.height <= 0 ||
            item.x + item.width > GRID_COLUMNS
        ) {
            return;
        }

        seenObjectIds.add(item.objectId);
        items.push(item);
    });

    return {
        schemaVersion: LAYOUT_SCHEMA_VERSION,
        columns: GRID_COLUMNS,
        items,
    };
}
